import { createSelector } from 'reselect';
import { chain, pick, get, findIndex, find } from 'lodash';

const EPISODE_NAV_FIELDS = ['id', 'title', 'titles', 'metadata'];

export const getDetailsSelector = (state) => state.details;

export const selectDetailsByContentId = createSelector(
  getDetailsSelector,
  (_state, props) => props.match.params.contentId,
  (details, contentId) => details[contentId],
);

export const selectLastFocus = createSelector(
  (_, props) => props.match.params.contentType,
  (state, props) => state.details[props.match.params.contentId],
  (_, props) => props.lastFocusKeeper,
  (contentType, details, lastFocusKeeper) => {
    const detailsId = get(details, ['data', 'id']);
    if (!contentType || !detailsId) return;

    const lastFocusKey = `details-${contentType}-${detailsId}`;
    return lastFocusKeeper.select(lastFocusKey);
  },
);

export const getCurrentVideoObjectDataByPropsSelector = createSelector(
  getDetailsSelector,
  (_state, props) => parseInt(props.match.params.videoId, 10) || null,
  (_state, props) => parseInt(props.match.params.seriesId, 10) || null,
  (_state, props) => parseInt(props.match.params.episodeId, 10) || null,

  (details, videoId, seriesId, episodeId) => {
    if (seriesId && details[seriesId]) {
      const seasons = get(details[seriesId], 'data.seasons');
      //Ищем сезон по episodeId
      const season = find(seasons, (season) =>
        find(get(season, 'episodes.content'), { id: episodeId }),
      );
      const episode = chain(season).get(['episodes', 'content']).find({ id: episodeId }).value();

      const seriesMetadata = {
        favorite: get(details[seriesId], 'data.favorite', false),
        id: seriesId,
      };

      return episode ? { ...episode, season, seriesMetadata } : null;
    } else {
      return get(details, [videoId, 'data'], null);
    }
  },
);

export const getDetailsNavigationSelector = createSelector(
  getDetailsSelector,
  (_state, props) => parseInt(props.match.params.videoId, 10) || null,
  (_state, props) => parseInt(props.match.params.seriesId, 10) || null,
  (_state, props) => parseInt(props.match.params.episodeId, 10) || null,

  (details, videoId, seriesId, episodeId) => {
    if (!episodeId || !seriesId || !details[seriesId]) return {};

    const navigation = {};

    const seasons = get(details[seriesId], 'data.seasons');
    //Ищем сезон по episodeId
    const season = find(seasons, (season) =>
      find(get(season, 'episodes.content'), { id: episodeId }),
    );

    const episodes = get(season, ['episodes', 'content']);

    if (episodes) {
      const currEpisodeIndex = findIndex(episodes, { id: episodeId });
      const prevEpisode = episodes[currEpisodeIndex - 1];
      const nextEpisode = episodes[currEpisodeIndex + 1];

      if (prevEpisode) navigation.prevEpisode = pick(prevEpisode, EPISODE_NAV_FIELDS);
      if (nextEpisode) navigation.nextEpisode = pick(nextEpisode, EPISODE_NAV_FIELDS);
    }

    return navigation;
  },
);

export const selectDetailsRelated = createSelector(
  getDetailsSelector,
  (_state, props) => props.contentId,
  (details = {}, contentId) => {
    return get(details[contentId], ['related'], []);
  },
);

export const selectDetailsRecemmendations = createSelector(
  getDetailsSelector,
  (_state, props) => props.contentId,
  (details = {}, contentId) => get(details[contentId], ['recommendations']),
);

export const selectExtraDetailsContentLists = createSelector(
  selectDetailsRelated,
  selectDetailsRecemmendations,
  (related, recommendations) => {
    const result = [...related];
    // Unlike "related" "recommendations" store as an object
    if (recommendations) result.push(recommendations);
    return result;
  },
);

export const getVodTypeSelector = createSelector(
  getDetailsSelector,
  (_, videoId) => videoId,
  (details = {}, videoId) => get(details[videoId], ['data', 'visibilityVodType']),
);
